/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { ECSPER_DOCUMENT_NAMESPACE } from './constants.js';
import * as officeXml from './office-xml.js';
import type { EcsperDocumentContext } from './schema-types.js';
import { buildEcsperDocumentXml } from './xml-builder.js';
import { parseEcsperDocumentXml } from './xml-parser.js';

export type { EcsperDocumentContext } from './schema-types.js';

/**
 * Read the Ecsper document context from the current Word document.
 *
 * @returns The document context, or null if the document has no Ecsper custom XML part
 */
export async function readDocumentContext(): Promise<EcsperDocumentContext | null> {
  const ids = await officeXml.findPartsByNamespace(ECSPER_DOCUMENT_NAMESPACE);
  const firstId = ids[0];
  if (firstId === undefined) {
    return null;
  }
  const xml = await officeXml.getPartXml(firstId);
  return parseEcsperDocumentXml(xml);
}

/**
 * Write (or update) the Ecsper document context. Existing parts in the namespace are removed first.
 */
export async function writeDocumentContext(context: EcsperDocumentContext): Promise<void> {
  const ids = await officeXml.findPartsByNamespace(ECSPER_DOCUMENT_NAMESPACE);
  for (const id of ids) {
    await officeXml.deletePart(id);
  }
  await officeXml.addPart(buildEcsperDocumentXml(context));
}

/** Whether the document contains any custom XML part in the Ecsper namespace */
export async function hasDocumentContext(): Promise<boolean> {
  const ids = await officeXml.findPartsByNamespace(ECSPER_DOCUMENT_NAMESPACE);
  return ids.length > 0;
}
